import { z } from 'zod';
import {
  channelAccountStatusSchema,
  channelAccountTypeSchema,
  languageSchema,
  nameSchema,
  objectIdSchema,
  type ChannelAccount,
} from '@hero/schema';
import { API } from '..';

/**
 * What staff see of a `ChannelAccount`. `credentialsRef` (an env-var
 * name) stays server-side.
 */
export type ChannelAccountAdminView = Omit<ChannelAccount, 'credentialsRef'>;

/** `GET /agent/admin/channel-accounts` — every connected line / inbox. SuperAdmin. */
export const channelAccountAdminListAPISchema = z.object({
  type: channelAccountTypeSchema.optional().describe('Only this channel type (e.g. `whatsapp`).'),
  status: channelAccountStatusSchema.optional().describe('Only this status (e.g. `active`).'),
  isSystemChannel: z
    .preprocess(v => (v === 'true' ? true : v === 'false' ? false : v), z.boolean())
    .optional()
    .describe('Only Hero-owned lines (`true`) or only org-owned (`false`).'),
});
export type ChannelAccountAdminListAPI = API<
  z.infer<typeof channelAccountAdminListAPISchema>,
  ChannelAccountAdminView[]
>;

/**
 * `PATCH /agent/admin/channel-accounts` — staff labels and the summary
 * language. Routing fields (`externalId`, `provider`, `credentialsRef`,
 * persona) are seeded and never edited from the panel. Empty string
 * clears a label.
 */
export const channelAccountAdminUpdateAPISchema = z.object({
  _id: objectIdSchema,
  title: z.union([nameSchema, z.literal('')]).optional().describe('Staff label, e.g. "Wathiqa Plus — main line".'),
  description: z.string().max(1000).optional().describe('What this account is for, who answers it.'),
  aiSummaryLanguage: languageSchema
    .optional()
    .describe('Language staff read voice-note summaries in on this line (platform lines; org lines use the org setting first).'),
});
export type ChannelAccountAdminUpdateAPI = API<
  z.infer<typeof channelAccountAdminUpdateAPISchema>,
  ChannelAccountAdminView
>;
